// The shell every Remote Punch window shares: a centered dialog portaled to
// <body> (page headers use backdrop-filter, which would otherwise trap a fixed
// overlay), closed by Esc, the X or a click outside, page scroll locked.
// Looks like the other Attendance dialogs (Shift Control, Default Location):
// it is built from the same style factory.
import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { ACCENT, makeStyles, scrollbarCss } from "../../pages/(Admin)Attendance/components/shiftControl/shiftControlStyles";

export const dialogColors = (dark) => ({
  dark,
  panel: dark ? "#1e293b" : "#ffffff",
  border: dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)",
  text: dark ? "#f1f5f9" : "#0f172a",
  muted: dark ? "#94a3b8" : "#64748b",
  soft: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
  primary: ACCENT,
  danger: "#ef4444",
  warning: "#d97706",
  success: "#22c55e",
});

export default function RemotePunchDialog({
  open = true,
  onClose,
  title,
  subtitle,
  icon: Icon,
  iconColor = ACCENT,
  dark = false,
  language = "en",
  maxWidth = 560,
  children,
  footer,
  zIndex = 10000,
  closeLabel = "Close",
}) {
  const styles = useMemo(() => makeStyles(dark ? "dark" : "light", language === "ar"), [dark, language]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // An open dropdown / calendar / alert on top closes first.
      if (document.querySelector("[data-modal-root='true'], .reusable-dropdown-portal, [data-datepicker-portal='true']")) return;
      e.stopPropagation();
      onClose?.();
    };
    window.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div onClick={onClose} style={{ ...styles.overlay, zIndex }}>
      <style>{scrollbarCss(styles.dark)}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{ ...styles.dialog, width: `min(${maxWidth}px, 100%)`, height: "auto" }}
      >
        <div style={styles.header}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0, flex: "1 1 240px" }}>
            {Icon && (
              <div style={{ ...styles.headerIcon, background: `linear-gradient(135deg, ${iconColor}, ${iconColor})`, boxShadow: `0 4px 14px ${iconColor}55` }}>
                <Icon size={22} />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <h2 style={styles.title}>{title}</h2>
              {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
            </div>
          </div>
          <button type="button" className="sc-close" style={styles.closeBtn} onClick={onClose} aria-label={closeLabel}>
            <X size={22} />
          </button>
        </div>
        <div className="sc-noscroll" style={{ ...styles.body, display: "block" }}>{children}</div>
        {footer && <div style={{ padding: "14px 24px", borderTop: `1px solid ${dialogColors(dark).border}`, flexShrink: 0 }}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
